import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck } from 'lucide-react';
import { useEffect, useState } from 'react';

import { conectarMeta, type CadastroMeta } from '@/api/secretario';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import {
  MetaSignupCancelled,
  openMetaSignup,
  preloadMetaSdk,
  type MetaSignupResult,
} from '@/lib/meta/cadastroEmbutido';
import { queryKeys } from '@/lib/pwa/queryConfig';

interface ConectarPelaMetaProps {
  cadastro: CadastroMeta;
}

/**
 * Conexão oficial, pela Meta (cadastro embutido): o advogado entra com a conta do
 * Facebook e escolhe o número numa janela da própria Meta
 * (docs/PLANO_SECRETARIO_WHATSAPP_META.md, Bloco C). Duas formas: manter o app
 * WhatsApp Business no celular (coexistência) ou um número só para o Secretário.
 */
export function ConectarPelaMeta({ cadastro }: ConectarPelaMetaProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [abrindo, setAbrindo] = useState<'app' | 'novo' | null>(null);
  const [sdkPronto, setSdkPronto] = useState(false);
  const [erroSdk, setErroSdk] = useState<string | null>(null);

  // O SDK carrega ao abrir o passo, não no clique: a janela da Meta só abre se o
  // clique a chamar na hora (ver ``openMetaSignup``).
  const appId = cadastro.app_id ?? '';
  const configId = cadastro.config_id ?? '';
  const versao = cadastro.versao ?? '';
  useEffect(() => {
    let ativo = true;
    preloadMetaSdk({ appId, configId, version: versao }).then(
      () => ativo && setSdkPronto(true),
      (erro: Error) => ativo && setErroSdk(erro.message),
    );
    return () => {
      ativo = false;
    };
  }, [appId, configId, versao]);

  const conectar = useMutation({
    mutationFn: async ({
      cadastroAberto,
      keepBusinessApp,
    }: {
      cadastroAberto: Promise<MetaSignupResult>;
      keepBusinessApp: boolean;
    }) => {
      const resultado = await cadastroAberto;
      return conectarMeta({
        codigo: resultado.code,
        waba_id: resultado.wabaId,
        phone_number_id: resultado.phoneNumberId,
        app_business: keepBusinessApp,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });
      showSuccess('WhatsApp conectado.');
    },
    onError: (error) => {
      if (error instanceof MetaSignupCancelled) return;
      notifyMutationError(error, showError);
    },
    onSettled: () => setAbrindo(null),
  });

  const abrir = (forma: 'app' | 'novo') => {
    const keepBusinessApp = forma === 'app';
    // Síncrono, dentro do clique: é isto que abre a janela da Meta.
    const cadastroAberto = openMetaSignup({ configId, keepBusinessApp });
    setAbrindo(forma);
    conectar.mutate({ cadastroAberto, keepBusinessApp });
  };
  const indisponivel = !sdkPronto || conectar.isPending;

  return (
    <div className="space-y-3 rounded-lg border border-primary/30 p-4">
      <div className="flex items-start gap-3">
        <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="space-y-1 text-sm">
          <p className="font-medium">Conexão oficial do WhatsApp (recomendada)</p>
          <p className="text-muted-foreground">
            Você entra com a sua conta do Facebook e escolhe o número numa janela da Meta. Depois, adicione uma forma de
            pagamento no Gerenciador do WhatsApp: a Meta cobra pelas mensagens que o Secretário inicia, como lembretes.
            Responder a quem escreveu é gratuito.
          </p>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Button type="button" onClick={() => abrir('app')} disabled={indisponivel} className="h-auto py-3">
          {abrindo === 'app' && <Spinner size={16} className="mr-2 text-inherit" />}
          <span className="text-left">
            Já uso o WhatsApp Business
            <span className="block text-xs font-normal opacity-90">e quero continuar usando o app no celular</span>
          </span>
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => abrir('novo')}
          disabled={indisponivel}
          className="h-auto py-3"
        >
          {abrindo === 'novo' && <Spinner size={16} className="mr-2 text-inherit" />}
          <span className="text-left">
            Número só para o Secretário
            <span className="block text-xs font-normal text-muted-foreground">sem WhatsApp no celular</span>
          </span>
        </Button>
      </div>
      {erroSdk && <p className="text-xs text-destructive">{erroSdk}</p>}
      <p className="text-xs text-muted-foreground">
        Mantendo o app, desligue a mensagem de saudação e a de ausência do WhatsApp Business: o Secretário as entenderia
        como você mesmo respondendo.
      </p>
    </div>
  );
}
